import { sampleTerrainRadius, heightAt } from '@/world/terrain'
import { findVillageSites } from '@/world/villages'
import type { VillagePlacement } from '@/world/villages'
import { MobType, VillagerJob } from './types'
import type { MobData, VillageSimData } from './types'

const RADIUS = 10_000

// ─── Seeded RNG ─────────────────────────────────────────────────────
function mulberry32(seed: number) {
  let s = seed | 0
  return () => {
    s = (s + 0x6D2B79F5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// ─── Sphere offset helper ───────────────────────────────────────────
function offsetOnSphere(
  nx: number, ny: number, nz: number,
  angularDist: number, azimuth: number,
): { nx: number; ny: number; nz: number } {
  const absNy = Math.abs(ny)
  let ex: number, ey: number, ez: number
  if (absNy < 0.9) {
    ex = -nz; ey = 0; ez = nx
  } else {
    ex = 0; ey = nz; ez = -ny
  }
  const el = Math.sqrt(ex * ex + ey * ey + ez * ez)
  ex /= el; ey /= el; ez /= el

  const fx = ny * ez - nz * ey
  const fy = nz * ex - nx * ez
  const fz = nx * ey - ny * ex

  const cosA = Math.cos(azimuth), sinA = Math.sin(azimuth)
  const dx = (ex * cosA + fx * sinA) * angularDist
  const dy = (ey * cosA + fy * sinA) * angularDist
  const dz = (ez * cosA + fz * sinA) * angularDist

  const ox = nx + dx, oy = ny + dy, oz = nz + dz
  const len = Math.sqrt(ox * ox + oy * oy + oz * oz)
  return { nx: ox / len, ny: oy / len, nz: oz / len }
}

// ─── Local-to-world for a village (same as VillageRenderer) ─────────
function villageLocalToNormal(
  village: VillagePlacement,
  localX: number,
  localZ: number,
): { nx: number; ny: number; nz: number } {
  const wx = village.position.x + village.tangentX.x * localX + village.tangentZ.x * localZ
  const wy = village.position.y + village.tangentX.y * localX + village.tangentZ.y * localZ
  const wz = village.position.z + village.tangentX.z * localX + village.tangentZ.z * localZ
  const len = Math.sqrt(wx * wx + wy * wy + wz * wz)
  return { nx: wx / len, ny: wy / len, nz: wz / len }
}

function createMob(
  type: MobType,
  subType: number,
  nx: number, ny: number, nz: number,
  villageIndex: number,
  heading: number,
  rng: () => number,
): MobData {
  return {
    type,
    subType,
    villageIndex,
    nx, ny, nz,
    radius: sampleTerrainRadius(nx, ny, nz),
    homeNx: nx, homeNy: ny, homeNz: nz,
    heading,
    speed: 0,
    state: 0,
    stateTimer: rng() * 3,
    wanderAngle: heading,
    targetNx: nx, targetNy: ny, targetNz: nz,
    hasTarget: false,
    animPhase: rng(),
    animSpeed: 0,
    mounted: false,
  }
}

// ─── Job assignment based on structure type ─────────────────────────
function jobForStructure(structureType: string): VillagerJob {
  switch (structureType) {
    case 'farm':         return VillagerJob.Farmer
    case 'armory':       return VillagerJob.Blacksmith
    case 'trading_post': return VillagerJob.Merchant
    case 'castle':       return VillagerJob.Guard
    case 'dock':         return VillagerJob.Fisherman
    case 'church':       return VillagerJob.Priest
    case 'pasture':      return VillagerJob.Shepherd
    case 'stable':       return VillagerJob.Shepherd
    default:             return VillagerJob.Farmer
  }
}

// ─── Varied job for house villagers (economy diversity) ─────────────
function jobForHouse(rng: () => number, _villageIndex: number): VillagerJob {
  // Distribute house villagers across economy roles
  const roll = rng()
  if (roll < 0.25) return VillagerJob.Farmer
  if (roll < 0.40) return VillagerJob.Miner
  if (roll < 0.55) return VillagerJob.Lumberjack
  if (roll < 0.65) return VillagerJob.Shepherd
  if (roll < 0.75) return VillagerJob.Blacksmith
  if (roll < 0.85) return VillagerJob.Merchant
  if (roll < 0.92) return VillagerJob.Fisherman
  return VillagerJob.Farmer
}

// ─── Extract VillageSimData from VillagePlacement ───────────────────
function toVillageSimData(village: VillagePlacement): VillageSimData {
  return {
    normal: { x: village.normal.x, y: village.normal.y, z: village.normal.z },
    position: { x: village.position.x, y: village.position.y, z: village.position.z },
    tangentX: { x: village.tangentX.x, y: village.tangentX.y, z: village.tangentX.z },
    tangentZ: { x: village.tangentZ.x, y: village.tangentZ.y, z: village.tangentZ.z },
    structures: village.layout.structures.map(s => ({
      type: s.type,
      localX: s.localX,
      localZ: s.localZ,
      rotation: s.rotation,
    })),
    roads: village.layout.paths.map(p => ({
      fromX: p.fromX,
      fromZ: p.fromZ,
      toX: p.toX,
      toZ: p.toZ,
    })),
  }
}

// ─── Spawn mobs for a single village ────────────────────────────────
function spawnVillageMobs(
  village: VillagePlacement,
  villageIndex: number,
  rng: () => number,
): MobData[] {
  const mobs: MobData[] = []

  for (const structure of village.layout.structures) {
    const { nx, ny, nz } = villageLocalToNormal(village, structure.localX, structure.localZ)

    switch (structure.type) {
      case 'pasture': {
        const count = 3 + Math.floor(rng() * 4)
        for (let i = 0; i < count; i++) {
          const angDist = (2 + rng() * 8) / RADIUS  // 2-10 units from center (within fence)
          const azimuth = rng() * Math.PI * 2
          const offset = offsetOnSphere(nx, ny, nz, angDist, azimuth)
          const mob = createMob(
            MobType.Sheep, Math.floor(rng() * 3),
            offset.nx, offset.ny, offset.nz,
            villageIndex, rng() * Math.PI * 2, rng,
          )
          // Anchor home to pasture center so sheep orbit within the fence
          mob.homeNx = nx
          mob.homeNy = ny
          mob.homeNz = nz
          mobs.push(mob)
        }
        // Shepherd villager at pasture
        {
          const angDist = (3 + rng() * 5) / RADIUS
          const azimuth = rng() * Math.PI * 2
          const offset = offsetOnSphere(nx, ny, nz, angDist, azimuth)
          mobs.push(createMob(
            MobType.Villager, VillagerJob.Shepherd,
            offset.nx, offset.ny, offset.nz,
            villageIndex, structure.rotation, rng,
          ))
        }
        break
      }
      case 'barn':
      case 'stable': {
        const count = 2 + Math.floor(rng() * 3)
        for (let i = 0; i < count; i++) {
          const angDist = (3 + rng() * 8) / RADIUS
          const azimuth = rng() * Math.PI * 2
          const offset = offsetOnSphere(nx, ny, nz, angDist, azimuth)
          mobs.push(createMob(
            MobType.Chicken, Math.floor(rng() * 3),
            offset.nx, offset.ny, offset.nz,
            villageIndex, rng() * Math.PI * 2, rng,
          ))
        }
        break
      }
      case 'house': {
        const angDist = (2 + rng() * 3) / RADIUS
        const azimuth = rng() * Math.PI * 2
        const offset = offsetOnSphere(nx, ny, nz, angDist, azimuth)
        mobs.push(createMob(
          MobType.Villager, jobForHouse(rng, villageIndex),
          offset.nx, offset.ny, offset.nz,
          villageIndex, structure.rotation, rng,
        ))
        break
      }
      case 'castle': {
        // Castle gets its assigned villager (Guard) plus a garrison
        const castleAng = (2 + rng() * 3) / RADIUS
        const castleAz = rng() * Math.PI * 2
        const castleOff = offsetOnSphere(nx, ny, nz, castleAng, castleAz)
        mobs.push(createMob(
          MobType.Villager, VillagerJob.Guard,
          castleOff.nx, castleOff.ny, castleOff.nz,
          villageIndex, structure.rotation, rng,
        ))
        // Spawn 3-5 guards around the castle
        const castleGuards = 3 + Math.floor(rng() * 3)
        for (let g = 0; g < castleGuards; g++) {
          const gAng = (5 + rng() * 10) / RADIUS
          const gAz = rng() * Math.PI * 2
          const gOff = offsetOnSphere(nx, ny, nz, gAng, gAz)
          mobs.push(createMob(
            MobType.Villager, VillagerJob.Guard,
            gOff.nx, gOff.ny, gOff.nz,
            villageIndex, rng() * Math.PI * 2, rng,
          ))
        }
        break
      }
      case 'armory': {
        // Blacksmith at the armory
        const armAng = (2 + rng() * 3) / RADIUS
        const armAz = rng() * Math.PI * 2
        const armOff = offsetOnSphere(nx, ny, nz, armAng, armAz)
        mobs.push(createMob(
          MobType.Villager, VillagerJob.Blacksmith,
          armOff.nx, armOff.ny, armOff.nz,
          villageIndex, structure.rotation, rng,
        ))
        // Spawn 2-3 guards near the armory
        const armGuards = 2 + Math.floor(rng() * 2)
        for (let g = 0; g < armGuards; g++) {
          const gAng = (4 + rng() * 8) / RADIUS
          const gAz = rng() * Math.PI * 2
          const gOff = offsetOnSphere(nx, ny, nz, gAng, gAz)
          mobs.push(createMob(
            MobType.Villager, VillagerJob.Guard,
            gOff.nx, gOff.ny, gOff.nz,
            villageIndex, rng() * Math.PI * 2, rng,
          ))
        }
        break
      }
      case 'farm':
      case 'trading_post':
      case 'dock':
      case 'church': {
        const angDist = (2 + rng() * 3) / RADIUS
        const azimuth = rng() * Math.PI * 2
        const offset = offsetOnSphere(nx, ny, nz, angDist, azimuth)
        mobs.push(createMob(
          MobType.Villager, jobForStructure(structure.type),
          offset.nx, offset.ny, offset.nz,
          villageIndex, structure.rotation, rng,
        ))
        break
      }
    }
  }

  return mobs
}

// ─── Spawn wild horses & wolves in wilderness ───────────────────────
function spawnWildMobs(
  villages: VillagePlacement[],
  rng: () => number,
): MobData[] {
  const mobs: MobData[] = []

  // Fibonacci sphere sampling for candidate positions
  const NUM_CANDIDATES = 2000
  const goldenAngle = Math.PI * (3 - Math.sqrt(5))
  const candidates: Array<{ nx: number; ny: number; nz: number }> = []

  for (let i = 0; i < NUM_CANDIDATES; i++) {
    const y = 1 - (2 * i) / (NUM_CANDIDATES - 1)
    const r = Math.sqrt(1 - y * y)
    const theta = goldenAngle * i
    const nx = Math.cos(theta) * r
    const ny = y
    const nz = Math.sin(theta) * r

    // Must be on land
    const h = heightAt(nx, ny, nz)
    if (h < 0.005) continue

    // Must be away from all villages (>150 units angular distance)
    let nearVillage = false
    for (const v of villages) {
      const dot = nx * v.normal.x + ny * v.normal.y + nz * v.normal.z
      if (dot > 0.9998) { nearVillage = true; break }  // ~0.02 rad = ~200 units
    }
    if (nearVillage) continue

    candidates.push({ nx, ny, nz })
  }

  // Pick horse herd locations from candidates (~100 horses total)
  const HORSE_HERDS = Math.min(20, Math.floor(candidates.length / 5))
  const usedIndices = new Set<number>()

  for (let h = 0; h < HORSE_HERDS; h++) {
    let idx: number
    let attempts = 0
    do {
      idx = Math.floor(rng() * candidates.length)
      attempts++
    } while (usedIndices.has(idx) && attempts < 50)
    if (usedIndices.has(idx)) continue
    usedIndices.add(idx)

    const center = candidates[idx]
    const herdSize = 4 + Math.floor(rng() * 3)  // 4-6 horses per herd

    for (let i = 0; i < herdSize; i++) {
      const angDist = (3 + rng() * 15) / RADIUS
      const azimuth = rng() * Math.PI * 2
      const offset = offsetOnSphere(center.nx, center.ny, center.nz, angDist, azimuth)
      const mob = createMob(
        MobType.Horse, Math.floor(rng() * 3),
        offset.nx, offset.ny, offset.nz,
        -1, rng() * Math.PI * 2, rng,
      )
      mob.homeNx = center.nx
      mob.homeNy = center.ny
      mob.homeNz = center.nz
      mobs.push(mob)
    }
  }

  // Pick wolf pack locations (different from horse herds)
  const WOLF_PACKS = Math.min(10, Math.floor(candidates.length / 10))

  for (let w = 0; w < WOLF_PACKS; w++) {
    let idx: number
    let attempts = 0
    do {
      idx = Math.floor(rng() * candidates.length)
      attempts++
    } while (usedIndices.has(idx) && attempts < 50)
    if (usedIndices.has(idx)) continue
    usedIndices.add(idx)

    const center = candidates[idx]
    const packSize = 2 + Math.floor(rng() * 3)  // 2-4 wolves per pack

    for (let i = 0; i < packSize; i++) {
      const angDist = (2 + rng() * 10) / RADIUS
      const azimuth = rng() * Math.PI * 2
      const offset = offsetOnSphere(center.nx, center.ny, center.nz, angDist, azimuth)
      const mob = createMob(
        MobType.Wolf, 0,
        offset.nx, offset.ny, offset.nz,
        -1, rng() * Math.PI * 2, rng,
      )
      mob.homeNx = center.nx
      mob.homeNy = center.ny
      mob.homeNz = center.nz
      mobs.push(mob)
    }
  }

  return mobs
}

// ─── Spawn all mobs for all villages ────────────────────────────────
export function spawnAllMobs(maxVillages: number = 30): {
  mobs: MobData[]
  villages: VillageSimData[]
} {
  const rng = mulberry32(99999)
  const placements = findVillageSites(maxVillages)
  const allMobs: MobData[] = []
  const villages: VillageSimData[] = []

  for (let vi = 0; vi < placements.length; vi++) {
    villages.push(toVillageSimData(placements[vi]))
    const villageMobs = spawnVillageMobs(placements[vi], vi, rng)
    allMobs.push(...villageMobs)
  }

  // Spawn wild horses and wolves in wilderness
  const wildMobs = spawnWildMobs(placements, rng)
  allMobs.push(...wildMobs)

  console.log(
    `[Mobs] Spawned ${allMobs.length} mobs: ` +
    `${allMobs.filter(m => m.type === MobType.Sheep).length} sheep, ` +
    `${allMobs.filter(m => m.type === MobType.Chicken).length} chickens, ` +
    `${allMobs.filter(m => m.type === MobType.Villager).length} villagers, ` +
    `${allMobs.filter(m => m.type === MobType.Horse).length} horses, ` +
    `${allMobs.filter(m => m.type === MobType.Wolf).length} wolves`,
  )

  return { mobs: allMobs, villages }
}
